import { CPT_DISPLAY_LIST } from 'Views/Common/enum';
import {
  GROUP_CHANGED,
  PLAN_UPDATED,
  PRODUCT_INFO_UPDATED,
  PLANS_BY_EXCEL,
  CPT_UPDATED,
  PLAN_DETAILS_UPDATED,
  TIER_UPDATED,
  REVSHARE_UPDATED,
  SUBSCRIBILI_COMMISSION_UPDATED,
  COMMISSION_UPDATED,
  VENDOR_PAYOUT_UPDATED,
  HIGHLIGHT_ERRORS,
  UPDATE_FIELDS_VALIDITY,
  UPDATE_PRICE_VALIDITY,
  SAVING_PLAN,
  SAVE_ERROR,
  PLAN_INFO_RECEIVED,
  RESET_PLAN,
  UPDATE_CATEGORY_DISCOUNTS,
  RESET_CATEGORY_DISCOUNTS,
  MEMBER_TIER_UPDATED,
  UPDATE_VALID_MEMBER_TO,
  CPT_CODES_RECEIVED,
  CPT_CODES_LOADING,
  CPT_CODES_ERROR,
  SET_SELECTED_TEMPLATE,
  UPDATE_PAYMENT_CATEGORY,
} from './actions';
import {
  EXT_PLAN_FREQUENCIES,
  SUBSCRIPTION_TYPE,
} from 'global-constants/constants';

const initialDiscounts = {
  initData: {},
  discount_level: 'global',
  discount_value: '',
  categories: [],
  services: [],
  custom: [],
  formUpdated: false,
  formValid: true,
};

const initialDataState = {
  productInfo: {
    plan_name: '',
    plan_package_type: 'Base',
    member_types: [SUBSCRIPTION_TYPE.CUSTOMER.value],
    description: '',
    statement_descriptor: '',
    allowed_age_max: 0,
    allowed_age_min: 0,
    additional_members: 0,
    addon_allowed: false,
    allow_future_dated: false,
    allowFutureDateToggle: true,
    renewal_plan_pkg_id: '',
    renewal_max_age_plan_pkg_id: '',
    plan_status: 'Active',
    disclosure_text: '',
    annual_savings_disclosure_text: '',
  },
  cpt_codes: [],
  templates: {
    data: [],
    loading: false,
    error: null,
  },
  plans: [],
  isValid: {
    group: false,
    product: false,
    cpt: true,
    priceValidity: true,
    memberTypeSelected: false,
  },
  isValidPrice: {
    0: { details: true, tiers: true, payout: true, member_tier: true },
  },
  highlightErrors: 0,
  isSavingPlan: false,
  discounts: { ...initialDiscounts },
  subs_payment_codes: [],
};

const initialState = {
  selectedGroup: '',
  isPlansByExcel: false,
  isValidMemberTo: true,
  selectedPriceModel: 'Single',
  initialProductInfo: initialDataState.productInfo,
  default_cpt_value: {
    list: 1,
    pdt_name: '',
    code: '',
    duplicate_code: '',
    description: '',
    quantity: '',
    frequency: 'annually',
    total_unit: '',
    retail_price: 0,
    subscription_price: 0,
    cpt_display: 'all',
  },
  default_tier_value: {
    up_to: '',
    unit_amount: '',
    upfront_amount: 0,
    signup_amount: 0,
  },
  default_commission: {
    partner: 0,
    group: 0,
    region: 0,
    location: 0,
    provider: 0,
  },
  default_plan: {
    plan_details: {
      member_type: 'General',
      billing_frequency: '',
      price_description: '',
      stripe_fee_paid_by: 'subscriber',
      private_only: false,
      patient_portal_view_only: false,
      status: 'Active',
    },
    tiers: [],
    revshare_payout: {
      enabled: false,
      revshare_id: '',
      payout_type: 'percent',
      amount: 0,
    },
    member_tier: {
      member_from: 1,
      member_to: 1,
      price_name: '',
    },
    subscribili_comm: {
      type: 'fixed',
      primary: 0,
      dependent: 0,
    },
    commission: {
      type: 'percent',
      values: [],
    },
    vendor_payout: {
      values: [
        {
          month: '',
          primary: 0,
          dependent: 0,
        },
      ],
    },
  },
  ...initialDataState,
};

const reducer = (state, { type, payload }) => {
  const getUpdatedPlan = (parameter) => {
    return state.plans.map((plan, index) =>
      index === payload.planIndex
        ? { ...plan, [parameter]: payload[parameter] }
        : plan,
    );
  };

  const applySelectedTemplate = (state, payload) => {
    if (payload.option === 'Add New') {
      return {
        ...state,
        cpt_codes: [state.default_cpt_value],
        discounts: {
          ...state.discounts,
          services: [],
        },
      };
    }

    const selectedTemplate = (state.templates.data || []).find(
      (template) => template.name === payload.option,
    );

    const inclusionCptCodes = [];
    const getCPTDisplayKey = (displayValue) => {
      const entry = Object.entries(CPT_DISPLAY_LIST).find(
        ([, value]) => value === displayValue,
      );
      return entry?.[0] || 'all';
    };

    const getFrequency = (frequency) => {
      if (frequency === 'Annual') {
        return 'annually';
      }
      const entry = Object.entries(EXT_PLAN_FREQUENCIES).find(
        ([, value]) => value === frequency,
      );
      return entry?.[0] || 'annually';
    };

    if (
      selectedTemplate?.inclusion &&
      Array.isArray(selectedTemplate.inclusion)
    ) {
      selectedTemplate.inclusion.forEach((category, listIndex) => {
        (category.codes || []).forEach((cpt) => {
          inclusionCptCodes.push({
            list: listIndex + 1,
            pdt_name: category.name || '',
            duplicate_code: cpt.duplicate_code || '',
            code: cpt.code || '',
            description: cpt.description || '',
            retail_price: cpt.retail_price ?? 0,
            subscription_price: cpt.subscription_price ?? 0,
            frequency: getFrequency(cpt.frequency),
            quantity: cpt.allowed_units ?? 'inf',
            total_unit: cpt.total_units ?? 'inf',
            cpt_display: getCPTDisplayKey(cpt.display_type),
          });
        });
      });
    }

    const customCptCodes = [];
    if (selectedTemplate?.custom && Array.isArray(selectedTemplate.custom)) {
      // Build a stable category_order for each custom section up-front so that
      // all codes belonging to the same section share the exact same value.
      const customSectionOrderMap = new Map();
      let customOrderCounter = 1;
      selectedTemplate.custom.forEach(({ name, category_order, order }) => {
        if (!customSectionOrderMap.has(name)) {
          const sectionOrder = category_order ?? order ?? customOrderCounter++;
          customSectionOrderMap.set(name, sectionOrder);
        }
      });

      selectedTemplate.custom.forEach((customSection) => {
        const sectionOrder = customSectionOrderMap.get(customSection.name);
        (customSection.codes || []).forEach((cpt) => {
          customCptCodes.push({
            category: customSection.name,
            category_order: sectionOrder,
            code: cpt.code || '',
            name: cpt.description || '',
            discount_type: cpt.discount_type || 'discount_percent',
            discount_value: cpt.discount_value ?? '',
          });
        });
      });
    }

    return {
      ...state,
      cpt_codes:
        inclusionCptCodes.length > 0
          ? inclusionCptCodes
          : [state.default_cpt_value],
      discounts: {
        ...state.discounts,
        services: customCptCodes,
      },
    };
  };

  switch (type) {
    case UPDATE_VALID_MEMBER_TO: {
      return {
        ...state,
        isValidMemberTo: payload.isValidMemberTo,
      };
    }
    case GROUP_CHANGED: {
      return {
        ...state,
        selectedGroup: payload.selectedGroup,
        isValid: {
          ...state.isValid,
          group: true,
        },
      };
    }
    case PLAN_UPDATED: {
      return {
        ...state,
        plans: payload.plans,
      };
    }
    case PRODUCT_INFO_UPDATED: {
      return {
        ...state,
        productInfo: payload.productInfo,
      };
    }
    case PLANS_BY_EXCEL: {
      return {
        ...state,
        isPlansByExcel: payload.isPlansByExcel,
      };
    }
    case CPT_UPDATED: {
      return {
        ...state,
        cpt_codes: payload.cpt_codes,
      };
    }
    case PLAN_DETAILS_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('plan_details'),
      };
    }
    case TIER_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('tiers'),
      };
    }
    case MEMBER_TIER_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('member_tier'),
      };
    }
    case REVSHARE_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('revshare_payout'),
      };
    }
    case SUBSCRIBILI_COMMISSION_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('subscribili_comm'),
      };
    }
    case COMMISSION_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('commission'),
      };
    }
    case VENDOR_PAYOUT_UPDATED: {
      return {
        ...state,
        plans: getUpdatedPlan('vendor_payout'),
      };
    }
    case HIGHLIGHT_ERRORS: {
      return {
        ...state,
        highlightErrors: state.highlightErrors + 1,
      };
    }
    case UPDATE_FIELDS_VALIDITY: {
      // Merged into the current state: each section sends only its own
      // fields, so validations dispatched in the same render don't
      // overwrite each other with an older snapshot.
      return {
        ...state,
        isValid: {
          ...state.isValid,
          ...payload.isValid,
          group: Boolean(state.selectedGroup),
        },
      };
    }
    case UPDATE_PRICE_VALIDITY: {
      return {
        ...state,
        isValidPrice: payload.isValidPrice,
      };
    }
    case SAVING_PLAN: {
      return {
        ...state,
        isSavingPlan: true,
      };
    }
    case SAVE_ERROR: {
      return {
        ...state,
        isSavingPlan: false,
      };
    }
    case PLAN_INFO_RECEIVED: {
      return {
        ...state,
        ...payload.planInfo,
        initialProductInfo: payload.planInfo.productInfo,
        subs_payment_codes: payload.planInfo.productInfo.payment_cpt_codes || [],
      };
    }
    case UPDATE_CATEGORY_DISCOUNTS: {
      return {
        ...state,
        discounts: {
          ...state.discounts,
          ...payload,
        },
      };
    }
    case RESET_CATEGORY_DISCOUNTS: {
      return {
        ...state,
        discounts: {
          ...initialDiscounts,
        },
      };
    }
    case RESET_PLAN: {
      return {
        ...initialState,
      };
    }
    case CPT_CODES_LOADING:
      return {
        ...state,
        templates: {
          ...state.templates,
          loading: true,
          error: null,
        },
      };
    case CPT_CODES_RECEIVED:
      return {
        ...state,
        templates: {
          ...state.templates,
          data: payload.cptTemplates,
          loading: false,
          error: null,
        },
      };
    case CPT_CODES_ERROR:
      return {
        ...state,
        templates: {
          ...state.templates,
          loading: false,
          error: payload.error,
        },
      };
    case SET_SELECTED_TEMPLATE: {
      return applySelectedTemplate(state, payload);
    }
    case UPDATE_PAYMENT_CATEGORY: {
      return {
        ...state,
        subs_payment_codes: payload.payment_codes || [],
      };
    }
    default:
      throw new Error();
  }
};

export { reducer, initialState };